import { Dropdown } from "../ui/dropdown/Dropdown";
import { Link } from "react-router";
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { UserCircle, LogOut, Settings } from "lucide-react";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/solid";
import { styles } from "../../styles/components/header/UserDropdown.styles";
import { useSettings } from "../../../src/context/SettingsContext";

// Define the UserData interface to match the expected type
interface UserData {
  first_name: string;
  last_name: string;
  email: string;
  profile_image?: string;
}

interface UserDropdownProps {
  userData?: UserData | null;
  setUserData?: React.Dispatch<React.SetStateAction<UserData | null>>;
}

const UserDropdown = ({
  userData: propUserData,
  setUserData: propSetUserData,
}: UserDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [userData, setUserData] = useState<UserData | null>(
    propUserData || null
  );
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const { settings } = useSettings();
  const location = useLocation();
  const toggleRef = useRef<HTMLButtonElement>(null); // Added toggleRef for the button

  useEffect(() => {
    const roles = sessionStorage.getItem("user_roles");
    if (roles) {
      try {
        const parsedRoles = JSON.parse(roles);
        setUserRoles(parsedRoles);
      } catch (error) {
        console.error("Error parsing user_roles from sessionStorage:", error);
        setUserRoles([]);
      }
    }
  }, []);

  useEffect(() => {
    // Close dropdown whenever route changes
    setIsOpen(false);
  }, [location.pathname]);

  // Check for dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains("dark"));
    };

    checkDarkMode();

    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  // Sync local userData with propUserData if provided
  useEffect(() => {
    if (propUserData !== undefined) {
      setUserData(propUserData);
    }
  }, [propUserData]);

  // Listen for profile update events
  useEffect(() => {
    const handleProfileUpdate = () => {
      setRefreshTrigger((prev) => prev + 1);
    };

    window.addEventListener("profileUpdated", handleProfileUpdate);

    return () => {
      window.removeEventListener("profileUpdated", handleProfileUpdate);
    };
  }, []);

  // Fetch user data if propUserData and propSetUserData are not provided
  const userId = sessionStorage.getItem("user_id");

  useEffect(() => {
    if (propUserData !== undefined && propSetUserData) {
      return;
    }

    if (!userId) {
      setError("User ID not found in session storage.");
      return;
    }

    const fetchUserProfile = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/profile/show`,
          { user_id: userId },
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("access_token")}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data.status === 200) {
          setUserData(response.data.data);
          if (propSetUserData) {
            propSetUserData(response.data.data);
          }
        } else {
          setError("Failed to fetch user data.");
        }
      } catch (error) {
        setError("Something went wrong while fetching data.");
      }
    };

    fetchUserProfile();
  }, [userId, propUserData, propSetUserData, refreshTrigger]);

  function toggleDropdown() {
    setIsOpen((prev) => !prev);
  }

  function closeDropdown() {
    setIsOpen(false);
  }

  const getInitials = (first?: string, last?: string) => {
    return `${first?.[0] || ""}${last?.[0] || ""}`.toUpperCase();
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  if (error) return <div className="text-red-500">{error}</div>;

  return (
    <div className={styles.container}>
      <button
        onClick={toggleDropdown}
        className={styles.button}
        ref={toggleRef} // Added toggleRef to the button
      >
        {userData?.profile_image ? (
          <img
            src={userData.profile_image}
            alt="Profile"
            className={`${styles.initialsCircle} object-cover rounded-full`}
          />
        ) : (
          <span
            className={styles.initialsCircle}
            style={{
              color: isDarkMode ? "#d1d5db" : settings.fontColor || "#000000",
            }}
          >
            {getInitials(userData?.first_name, userData?.last_name)}
          </span>
        )}

        <div className={styles.userInfoContainer}>
          <span
            className={styles.userName}
            style={{
              color: isDarkMode ? "#d1d5db" : settings.fontColor || "#000000",
            }}
          >
            {userData ? `Hi, ${userData.first_name}` : "Loading..."}
          </span>
          <span
            className={styles.userEmail}
            style={{
              color: isDarkMode ? "#d1d5db" : settings.fontColor || "#000000",
            }}
          >
            {userData ? userData.email : ""}
          </span>
        </div>

        {isOpen ? (
          <ChevronUpIcon
            className={styles.chevronUp}
            style={{
              color: isDarkMode ? "#d1d5db" : settings.fontColor || "#000000",
            }}
          />
        ) : (
          <ChevronDownIcon
            className={styles.chevronDown}
            style={{
              color: isDarkMode ? "#d1d5db" : settings.fontColor || "#000000",
            }}
          />
        )}
      </button>

      <Dropdown
        isOpen={isOpen}
        onClose={closeDropdown}
        className={styles.dropdown}
        toggleRef={toggleRef} // Pass toggleRef to Dropdown
      >
        <div className={styles.dropdownHeader}>
          <span
            className={styles.dropdownName}
            style={{
              color: isDarkMode ? "#d1d5db" : settings.fontColor || "#000000",
            }}
          >
            {userData
              ? `${userData.first_name} ${userData.last_name}`
              : "Loading..."}
          </span>
          <span
            className={styles.dropdownEmail}
            style={{
              color: isDarkMode ? "#d1d5db" : settings.fontColor || "#000000",
            }}
          >
            {userData ? userData.email : "Loading..."}
          </span>
        </div>
        <ul className={styles.dropdownList}>
          <li>
            <Link
              to="/profile"
              className={styles.dropdownItem}
              onClick={closeDropdown}
              style={{
                backgroundColor: isActive("/profile")
                  ? isDarkMode
                    ? "#374151"
                    : settings.menuActiveColor || "#f8f7f7"
                  : undefined,
                color: isDarkMode ? "#d1d5db" : settings.fontColor || "#000000",
              }}
            >
              <UserCircle
                className={styles.icon}
                style={{
                  color: isDarkMode
                    ? "#d1d5db"
                    : settings.fontColor || "#000000",
                }}
              />
              <span className={styles.itemText}>Profile</span>
            </Link>
          </li>
          {userRoles.includes("Admin") && (
            <li>
              <Link
                to="/settings-list"
                className={styles.dropdownItem}
                onClick={closeDropdown}
                style={{
                  backgroundColor: isActive("/settings-list")
                    ? isDarkMode
                      ? "#374151"
                      : settings.menuActiveColor || "#f8f7f7"
                    : undefined,
                  color: isDarkMode
                    ? "#d1d5db"
                    : settings.fontColor || "#000000",
                }}
              >
                <Settings
                  className={styles.icon}
                  style={{
                    color: isDarkMode
                      ? "#d1d5db"
                      : settings.fontColor || "#000000",
                  }}
                />
                <span className={styles.itemText}>Settings</span>
              </Link>
            </li>
          )}
          <li>
            <Link
              to="/logout"
              className={styles.dropdownItem}
              onClick={closeDropdown}
              style={{
                backgroundColor: isActive("/logout")
                  ? isDarkMode
                    ? "#374151"
                    : settings.menuActiveColor || "#f8f7f7"
                  : undefined,
                color: isDarkMode ? "#d1d5db" : settings.fontColor || "#000000",
              }}
            >
              <LogOut
                className={styles.icon}
                style={{
                  color: isDarkMode
                    ? "#d1d5db"
                    : settings.fontColor || "#000000",
                }}
              />
              <span className={styles.itemText}>Logout</span>
            </Link>
          </li>
        </ul>
      </Dropdown>
    </div>
  );
};

export default UserDropdown;
